import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';

interface AddCustomFieldModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (fieldName: string) => Promise<void>;
}

const AddCustomFieldModal: React.FC<AddCustomFieldModalProps> = ({
  open,
  onOpenChange,
  onAdd,
}) => {
  const { t } = useTranslation(['templates', 'common']);
  const [fieldName, setFieldName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!fieldName.trim()) return;
    try {
      setSaving(true);
      await onAdd(fieldName.trim());
      setFieldName('');
      onOpenChange(false);
    } catch {
      // Error handled by parent
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && fieldName.trim() && !saving) {
      handleSubmit();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setFieldName(''); }}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>{t('templates:custom_field_title')}</DialogTitle>
          <DialogDescription>{t('templates:custom_field_description')}</DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <Label htmlFor="custom-field-name">{t('templates:custom_field_name_label')}</Label>
          <Input
            id="custom-field-name"
            value={fieldName}
            onChange={(e) => setFieldName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('templates:custom_field_name_placeholder')}
            className="mt-1"
            autoFocus
            maxLength={100}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !fieldName.trim()}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t('templates:custom_field_add_button')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddCustomFieldModal;
